import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, LogIn } from 'lucide-react';
import { passwordSchema } from '@garlic/shared';
import { api, errorMessage, fieldErrors, post } from '../lib/api';
import { Alert, Button, Card, Field, GarlicMark, Input, Spinner } from '../ui/primitives';
import { RestaurantAvatar } from './RestaurantBadge';
import type { TenantRestaurant } from './TenantApp';
import { staffPath, publicPath } from '../lib/paths';

function AuthLayout({ restaurant: r, title, subtitle, children }: { restaurant: TenantRestaurant; title: string; subtitle?: string; children: ReactNode }) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center bg-paper-2 px-4 py-10">
      <div className="w-full max-w-sm">
        <Link to={publicPath()} className="mb-6 inline-flex items-center gap-1 text-sm text-ink-3 hover:text-ink">
          <ArrowLeft className="size-3.5" /> Voltar ao início
        </Link>
        <Card className="p-6">
          <div className="mb-5 flex items-center gap-3">
            <RestaurantAvatar r={r} className="size-11" />
            <div className="min-w-0">
              <p className="truncate text-sm text-ink-3">{r.name}</p>
              <h1 className="text-lg font-semibold leading-tight">{title}</h1>
            </div>
          </div>
          {subtitle && <p className="-mt-2 mb-4 text-sm text-ink-3">{subtitle}</p>}
          {children}
        </Card>
        <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-ink-3">
          <GarlicMark className="size-4" /> Garlic
        </p>
      </div>
    </main>
  );
}

export function StaffLogin({ restaurant }: { restaurant: TenantRestaurant }) {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await post('/api/staff/auth/login', { email, password });
      await qc.invalidateQueries({ queryKey: ['staff-me'] });
      const from = (location.state as { from?: string } | null)?.from;
      // Só volta para páginas do painel DESTE restaurante (nunca para um endereço vindo de fora).
      navigate(from?.startsWith(`${staffPath()}/`) || from === staffPath() ? from : staffPath(), { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout restaurant={restaurant} title="Área do Funcionário">
      <form onSubmit={submit} className="space-y-4">
        {restaurant.status === 'SUSPENDED' && <Alert tone="danger">Este estabelecimento está suspenso. Fale com o suporte do Garlic.</Alert>}
        {error && <Alert tone="danger">{error}</Alert>}
        <Field label="E-mail">{(id) => <Input id={id} type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />}</Field>
        <Field label="Senha">{(id) => <Input id={id} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />}</Field>
        <Button type="submit" variant="brand" className="w-full" loading={busy} icon={<LogIn className="size-4" />}>Entrar</Button>
        <p className="text-center text-sm">
          <Link to={staffPath('/recuperar')} className="text-ink-3 hover:text-ink hover:underline">Esqueci minha senha</Link>
        </p>
      </form>
    </AuthLayout>
  );
}

export function ForgotPassword({ restaurant }: { restaurant: TenantRestaurant }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<string>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const r = await post<{ message: string }>('/api/staff/auth/forgot', { email });
      setSent(r.message);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout restaurant={restaurant} title="Recuperar senha" subtitle="Enviaremos um link para você criar uma nova senha.">
      {sent ? (
        <div className="space-y-4">
          <Alert tone="success">{sent}</Alert>
          <Link to={staffPath('/login')}><Button variant="secondary" className="w-full">Voltar ao login</Button></Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {error && <Alert tone="danger">{error}</Alert>}
          <Field label="E-mail">{(id) => <Input id={id} type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />}</Field>
          <Button type="submit" variant="brand" className="w-full" loading={busy}>Enviar link</Button>
        </form>
      )}
    </AuthLayout>
  );
}

/** Convite (primeiro acesso) e redefinição de senha usam o mesmo fluxo de token de uso único. */
export function SetPassword({ restaurant }: { restaurant: TenantRestaurant }) {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const info = useQuery({
    queryKey: ['auth-token', token],
    queryFn: () => api<{ purpose: 'INVITE' | 'PASSWORD_RESET'; name: string; email: string }>(`/api/staff/auth/token?token=${encodeURIComponent(token)}`),
    enabled: token.length >= 20,
    retry: false,
  });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const check = passwordSchema.safeParse(password);
    if (!check.success) return setErrors({ password: check.error.issues[0]!.message });
    if (password !== confirm) return setErrors({ confirm: 'As senhas não conferem' });
    setBusy(true);
    setError(undefined);
    try {
      await post('/api/staff/auth/complete', { token, password });
      await qc.invalidateQueries({ queryKey: ['staff-me'] });
      navigate(staffPath(), { replace: true });
    } catch (err) {
      setErrors(fieldErrors(err));
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const isInvite = info.data?.purpose === 'INVITE';
  return (
    <AuthLayout restaurant={restaurant} title={isInvite ? 'Bem-vindo(a) à equipe' : 'Criar nova senha'}>
      {token.length < 20 || info.isError ? (
        <div className="space-y-4">
          <Alert tone="danger">{info.isError ? errorMessage(info.error) : 'Link inválido.'}</Alert>
          <Link to={staffPath('/recuperar')}><Button variant="secondary" className="w-full">Pedir um novo link</Button></Link>
        </div>
      ) : info.isPending ? (
        <Spinner />
      ) : (
        <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-sm text-ink-2">
            Olá, <strong>{info.data.name}</strong> ({info.data.email}). {isInvite ? 'Defina sua senha para acessar o sistema.' : 'Escolha uma nova senha.'}
          </p>
          {error && !Object.keys(errors).length && <Alert tone="danger">{error}</Alert>}
          <Field label="Nova senha" error={errors.password} hint="Mínimo de 10 caracteres, com letras e números.">
            {(id, d) => <Input id={id} aria-describedby={d} type="password" autoComplete="new-password" value={password} invalid={!!errors.password} onChange={(e) => { setPassword(e.target.value); setErrors({}); }} />}
          </Field>
          <Field label="Confirme a senha" error={errors.confirm}>
            {(id, d) => <Input id={id} aria-describedby={d} type="password" autoComplete="new-password" value={confirm} invalid={!!errors.confirm} onChange={(e) => { setConfirm(e.target.value); setErrors({}); }} />}
          </Field>
          <Button type="submit" variant="brand" className="w-full" loading={busy}>{isInvite ? 'Criar senha e entrar' : 'Salvar nova senha'}</Button>
        </form>
      )}
    </AuthLayout>
  );
}
